import React from 'react';
import ReactDOM from 'react-dom';
import './index.css';
import './themeStyles.css';
import App from './App';
import * as serviceWorker from './serviceWorker';
import thunk from 'redux-thunk';
import { createLogger } from 'redux-logger';
import {applyMiddleware, combineReducers, createStore} from 'redux';
import {Provider} from 'react-redux';
import * as reducers from './reducers';
import {BrowserRouter} from 'react-router-dom';
import {SnackbarProvider} from 'notistack';
import Button from "@material-ui/core/Button";
import {secureGetItem, secureSetItem} from './secureStorage';
import logger from './logger';

// Admin 认证状态持久化中间件（🔒 安全增强：使用加密存储）
const adminAuthPersistence = store => next => action => {
  const result = next(action);
  
  // 当 Admin 认证状态改变时，保存到 sessionStorage（加密）
  if (action.type === Symbol.for('SET_ADMIN_AUTHENTICATED')) {
    const state = store.getState();
    try {
      // 🔒 使用加密存储替代明文 sessionStorage
      secureSetItem('adminAuth', {
        authenticated: state.adminAuthenticated,
        timestamp: Date.now()
      });
    } catch (e) {
      console.warn('Failed to save admin auth state:', e);
    }
  }
  
  return result;
};

// 从加密存储恢复初始状态（🔒 安全增强）
const loadPersistedState = () => {
  try {
    // 🔒 使用加密存储读取
    const authData = secureGetItem('adminAuth', null);
    
    if (authData && authData.authenticated && authData.timestamp) {
      const elapsed = Date.now() - authData.timestamp;
      const CACHE_TIME = 2 * 60 * 60 * 1000; // 2 小时（与固件保持一致）
      
      if (elapsed < CACHE_TIME) {
        logger.debug(`✓ Restored admin auth from secure storage (${Math.floor(elapsed / 1000)}s ago)`);
        return { adminAuthenticated: authData.authenticated };
      } else {
        logger.debug('✗ Admin auth cache expired, clearing storage');
        secureSetItem('adminAuth', null);
      }
    }
    
    // 清理旧的明文 sessionStorage（兼容性处理）
    sessionStorage.removeItem('adminAuthenticated');
    sessionStorage.removeItem('adminAuthTimestamp');
  } catch (e) {
    logger.warn('Failed to load persisted state:', e);
  }
  return {};
};

const persistedState = loadPersistedState();

let middleware = [thunk, adminAuthPersistence];
if (process.env.NODE_ENV !== 'production') {
  // 配置 logger：只记录状态改变的 action，减少噪音
  const loggerMiddleware = createLogger({
    predicate: (getState, action) => {
      // 忽略频繁且不重要的 action
      if (action.type === Symbol.for('APPEND_APDU_LOG')) {
        return false; // 不记录 APDU 日志
      }
      return true;
    },
    diff: true, // 显示状态差异
    collapsed: true, // 默认折叠日志
  });
  middleware = [...middleware, loggerMiddleware];
}
const store = createStore(
  combineReducers(reducers),
  persistedState,
  applyMiddleware(...middleware)
);

const notistackRef = React.createRef();
const onClickDismiss = key => () => {
  notistackRef.current.closeSnackbar(key);
}

ReactDOM.render(
  <Provider store={store}>
    <BrowserRouter basename={typeof process !== 'undefined' && process.env ? process.env.PUBLIC_URL : ''}>
      <SnackbarProvider
        maxSnack={3} ref={notistackRef}
        action={(key) => (
          <Button onClick={onClickDismiss(key)}>
            Dismiss
          </Button>
        )}>
        <App/>
      </SnackbarProvider>
    </BrowserRouter>
  </Provider>,
  document.getElementById('root')
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.register();
